/**
 * Rendering a message body without giving it the run of the page.
 *
 * Mail is hostile input. Rather than sanitising HTML -- a parser-differential
 * arms race this project has no business entering -- the body is handed to a
 * sandboxed iframe, which puts it in an opaque origin with no form submission,
 * no access to this document and no access to `localStorage` where the tokens
 * are. Allowances are added back one at a time: popups, so a link the reader
 * clicks opens as an ordinary tab, and `allow-scripts`.
 *
 * **`allow-scripts` does not mean the message may run scripts.** It means the
 * frame is capable of running one, and the CSP inside the document names a
 * single SHA-256 -- so exactly one script executes, {@link FIT_SCRIPT}, which is
 * ours and measures the message so it can be scaled to fit a narrow screen. The
 * message's own scripts are refused, external and inline alike, and so are its
 * `onclick` attributes, because a hash source does not imply `unsafe-hashes`.
 *
 * The load-bearing omission is `allow-same-origin`, which is *not* granted and
 * must never be: the two together are not a sandbox with an extra permission,
 * they are no sandbox at all. `html.test.ts` asserts both that the hash still
 * matches its script and that the sandbox still lacks same-origin, because the
 * first fails silently and the second fails severely.
 *
 * A CSP inside the frame is the second layer. Without it a sandboxed frame
 * still *fetches* remote images and stylesheets, and those fetches are how a
 * sender learns the address is live.
 *
 * # The header is the app's; the frame carries a copy for paper
 *
 * Who a message is from is drawn by the reader, in the app's own document and
 * above this frame, where no stylesheet of the sender's can reach it. It lived
 * *inside* this document from 2026-08-17, so that it scrolled away with the
 * mail; the price was sharing a document with hostile markup, and the
 * message's own `<style>` arrives after ours. Every rule was made `!important`,
 * then the inheritable text properties were reset too, and then an eBay digest
 * whose reset zeroes `html, body` margins still shifted it. A header that has
 * to be defended against each new template is a header in the wrong document,
 * so it went back up to the app, and the sandbox is unchanged by the move.
 *
 * {@link messageHeader} still renders the same block into this document, but
 * hidden on screen and shown only under `@media print`. The frame prints
 * itself (see {@link FIT_SCRIPT}), so a printout of the app's header is not
 * possible, and a printed message without its sender and date is not a
 * printout of the message. On paper a sender's leaked styling is a cosmetic
 * risk; on screen it was the bug. The block carries no controls either way.
 */

import { darkenColours } from "./darken";

export interface RenderedBody {
  srcdoc: string;
  /** True when something remote was suppressed, so the reader can offer it. */
  hasRemoteContent: boolean;
}

const REMOTE = /(?:src|background|srcset)\s*=\s*["']?\s*(?:https?:)?\/\//iu;
const REMOTE_CSS = /url\(\s*["']?\s*(?:https?:)?\/\//iu;

/**
 * A `cid:` reference, wherever it appears: `src="cid:x"`, bare `src=cid:x`, or
 * CSS `url(cid:x)`. `\bcid:` so `acid:` and a path segment like `/cid:note`
 * that happens to be preceded by a word character are not caught; the id runs
 * to the delimiter that closes its attribute, its `url(...)`, or whitespace.
 * A stray match that is not a real embedded image is harmless -- see
 * {@link inlineCidImages}: an id that is not in the map is left untouched.
 */
const CID = /\bcid:([^"'\s>)<`]+)/giu;

/**
 * Replace `cid:` image references with URLs the frame can actually load.
 *
 * The twin of `inline_cid_images` in `core/src/render.rs`; read that for the
 * whole of the bug. In short: an embedded image points at its MIME part with
 * `<img src="cid:id">`, and nothing resolves it -- the sandboxed frame's CSP
 * permits `img-src data:` and `cid:` is not that, so the image is blank. The
 * shell fetches each inline part's bytes and hands this a map from Content-ID
 * to a `data:` URI; a `data:` URI is the one form that loads inside an
 * opaque-origin sandbox and needs no CSP change, because embedding bytes is not
 * a fetch and so reveals nothing to the sender. A `cid:` this cannot resolve is
 * left exactly as it was.
 */
export function inlineCidImages(html: string, resolve: Map<string, string>): string {
  if (resolve.size === 0 || !html.includes("cid:")) return html;
  return html.replace(CID, (whole, id: string) => resolve.get(id) ?? whole);
}

/**
 * Whether the message brought colours of its own.
 *
 * Any `color`/`bgcolor` attribute or `color`/`background*` property, in a
 * style block or inline. Deliberately loose: `border-color`, and even the word
 * "color:" in prose, count -- being wrong in that direction costs a message its
 * dark background, being wrong the other way costs the reader the text. See
 * {@link renderHtmlBody} for why the question is asked at all.
 */
const OWN_COLOURS = /(?:color|background)[\w-]*\s*[:=]/iu;

export function bringsOwnColours(html: string): boolean {
  return OWN_COLOURS.test(html);
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/gu, "&amp;")
    .replace(/</gu, "&lt;")
    .replace(/>/gu, "&gt;")
    .replace(/"/gu, "&quot;");
}

/**
 * The one script allowed to run inside a message, and the only one there will
 * ever be.
 *
 * It exists because no amount of CSS can make every message fit. The width cap
 * in {@link hardCap} took Amit's worst message from 359 elements past the right
 * edge to 16, and the survivors are table cells claiming no width at all, sized
 * by a layout algorithm our declarations get no vote in. Fitting those means
 * scaling the document, and scaling means knowing how wide it is -- a number
 * only something inside the frame can read.
 *
 * `zoom` rather than `transform: scale()`, and the difference is not cosmetic:
 * `transform` paints smaller without relaying out, so the document keeps its
 * old height and leaves a long tail of blank space below the message. `zoom`
 * is a layout-affecting scale, so the height comes down with the width.
 *
 * Measured with the zoom cleared and re-applied from scratch, because measuring
 * an already-scaled document answers a question about the scale rather than
 * about the content. Re-run on load and on resize: images arrive after first
 * paint and are the commonest reason a message is wider than it first looked,
 * and the frame changes width when the window does.
 *
 * **Do not edit this string casually.** Its SHA-256 is the CSP entry that lets
 * it run -- see {@link SCRIPT_HASH} -- so any change here is a change to that
 * hash, and the test in `html.test.ts` recomputes one from the other and fails
 * if they have drifted. A mismatch is not cosmetic: the script silently stops
 * running and messages quietly go back to being cut off.
 */
const FIT_SCRIPT = `(function(){function f(){var d=document.documentElement,b=document.body;b.style.zoom="";var n=Math.max(d.scrollWidth,b.scrollWidth),h=d.clientWidth;if(n>h+1){b.style.zoom=((h-1)/n).toFixed(4)}}f();addEventListener("load",f);addEventListener("resize",f);addEventListener("message",function(e){if(e.data==="hm-print"){try{print()}catch(_){}}})})();`;

/**
 * The SHA-256 of {@link FIT_SCRIPT}, base64, as CSP wants it.
 *
 * A constant rather than something computed here, because computing it needs
 * `crypto.subtle` and that is asynchronous -- and this function is called while
 * building a string, synchronously, on every render. `html.test.ts` does the
 * computing and asserts the two agree, which is where a drift is caught.
 *
 * Pinned in three places, and the frame's meta tag below is only one of them.
 * A `srcdoc` document runs under its parent's policy as well as its own, so
 * the *page* policies -- `deploy/security-headers.conf` for the browser build
 * and `src-tauri/tauri.conf.json` for the desktop -- must carry this hash too,
 * or the script is refused there with nothing on screen to say so. That is how
 * the browser build lost scaling and printing for a while. `dev/parity.mjs`
 * checks all three against this constant.
 */
const SCRIPT_HASH = "sha256-DQ5O9c6KDmbuADKvrpMmgYjaVW36gdXBZVHoyop2FSA=";

/**
 * What the message document is allowed to do.
 *
 * # Why there is a script hash here at all
 *
 * Because Amit asked for Outlook's zoom-to-fit and it cannot be done without
 * measuring the message. The narrowest way to buy that is this: the frame gains
 * `allow-scripts` but **not** `allow-same-origin`, so it runs in an opaque
 * origin and cannot reach this app's document, its storage or the device token;
 * and `script-src` names one hash, so the only script that may execute is
 * {@link FIT_SCRIPT}. The message's own scripts -- inline, external, or an
 * `onclick` attribute -- are all still refused, the last because a hash source
 * does not imply `unsafe-hashes`.
 *
 * That is a smaller grant than it sounds and a real one nonetheless. It was
 * `script-src 'none'` before, which needed no argument at all; this needs the
 * hash to stay correct and the sandbox to keep lacking `allow-same-origin`.
 * Both are asserted in `html.test.ts` rather than left to a reader's care,
 * because the failure is silent in one direction and severe in the other.
 */
function csp(allowRemoteImages: boolean): string {
  const img = allowRemoteImages ? "https: data:" : "data:";
  return [
    "default-src 'none'",
    `img-src ${img}`,
    "style-src 'unsafe-inline'",
    "font-src data:",
    `script-src '${SCRIPT_HASH}'`,
    "frame-src 'none'",
    "form-action 'none'",
    // Nothing may be sent anywhere. The script needs no network and the message
    // is not getting one -- without this, a hash-allowed script would still be
    // a place `fetch` exists.
    "connect-src 'none'",
  ].join("; ");
}

/**
 * Styles injected alongside the message.
 *
 * Deliberately minimal. Mail comes with its own design and overriding it makes
 * newsletters look broken; all this does is stop a body with no styling of its
 * own from rendering as 8pt Times on white inside a dark window, and stop a
 * fixed-width table from forcing a horizontal scrollbar on a phone.
 *
 * # The header inside the message, for paper only
 *
 * `.hm-head` styles the sender block that {@link messageHeader} renders into
 * this document. On screen it is `display: none`; the reader draws the header
 * in the app's document instead (see the module note). It is shown again under
 * `@media print`, because the frame prints itself and the printout would
 * otherwise have no sender and no date.
 *
 * Every rule carries `!important` because the message's own style element is
 * parsed after this one and would otherwise win at equal specificity -- the
 * same lesson the spacer below cost. A determined sender can still reach it;
 * nothing in it is a control, so the worst case is an ugly printed header
 * rather than a misleading button.
 *
 * That guard only covers properties this block *sets*. Inheritable text
 * properties it did not set -- `text-align`, `text-transform`, `letter-spacing`
 * and the like -- were unguarded, and a transactional layout that sets them on
 * `body` (`body { text-align: center; text-transform: uppercase }`, which
 * Subway's order confirmations and most centred marketing mail do) had them
 * *inherited* into the header: the name, date and recipient rows came out
 * centred and upper-cased. So `.hm-head` also resets that class of property.
 * `font` shorthand already resets style/weight/line-height and colour is set,
 * so those are covered; the reset carries down to the children, which override
 * none of it.
 *
 * # The trailing space at the bottom
 *
 * The rule at the end reserves room for the reader's floating toolbar on the
 * phone layout, and it has to live *in here* rather than in `styles.css`,
 * because this document is the one that scrolls: `.body-frame` is `height:
 * 100%` of a box that never scrolls vertically, so space added out there
 * shortens the message's viewport instead of extending its scroll. Tried that
 * way first, and it got both halves of the requirement backwards -- the mail
 * stopped above the dock instead of running under it, and there was no extra
 * scroll at the end either.
 *
 * It is an empty **element** rather than `padding-bottom` on `body`, and that
 * is not fussiness. This stylesheet is in `<head>`; the message's own `<style>`
 * blocks arrive later, inside `<body>`, so at equal specificity *they* win --
 * a Reddit digest sets its own `body` padding and swallowed the clearance
 * whole, which is how this was found: scrolled to the very end, the last line
 * of the address was still under the buttons. A spacer with a class of its own
 * outranks a bare `body` rule and carries `!important` against the ones that
 * do use a class. `aria-hidden`, because it is furniture.
 *
 * It sits inside `body` and so takes *its* background, which is not always the
 * background of whatever the message ends with -- a newsletter whose footer is
 * a grey table finishes with a band of the body colour under it. Accepted: the
 * alternative is guessing at the last element's background, and the band is
 * only ever seen at the very end of a scroll.
 *
 * The media query is the parent's docked-toolbar breakpoint, evaluated against
 * this frame's own width -- the reader column rather than the window. The two
 * are the same thing on a phone, which is where the dock exists. The honest
 * caveat: a desktop reading pane that happens to be 416-767px wide matches too
 * and gets blank space after the last line. Harmless, since its toolbar is at
 * the top, and the alternative was passing the clearance in as a fourth input
 * to {@link wrap} -- which changes the srcdoc, and so re-navigates the frame on
 * every fold, throwing away the reader's place in the message. The iframe key
 * in `ui/Reader.tsx` records why a re-navigation is not free.
 */
function frameStyle(dark: boolean): string {
  return `
    :root { color-scheme: ${dark ? "dark" : "light"}; }
    html, body {
      margin: 0; padding: 16px 20px;
      font: 15px/1.55 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      color: ${dark ? "#e6e8ec" : "#16181d"};
      background: ${dark ? "#15171c" : "#ffffff"};
      overflow-wrap: break-word;
    }
    img, video, table { max-width: 100%; }
    img { height: auto; }
    table { border-collapse: collapse; }
    pre { white-space: pre-wrap; overflow-x: auto; }
    /*
     * Undo the width somebody baked in twenty years of email ago.
     *
     * Marketing mail is laid out in tables with \`width="600"\` on the table and
     * often on every cell, because that is what worked in Outlook. On a 312px
     * cover screen that is a document twice the width of the window, and the
     * reader pans sideways to read one line at a time. \`max-width: 100%\` above
     * cannot fix it on its own: it caps the box, but a table whose *columns*
     * have been told they are 600px keeps a min-content width of 600px and
     * overflows anyway.
     *
     * So the widths are removed rather than capped. \`!important\` is doing
     * real work here and is not decoration -- the HTML \`width\` attribute is a
     * presentational hint that any rule outranks, but the same number usually
     * appears again in a \`style="width:600px"\`, and an inline declaration is
     * beaten by nothing weaker.
     *
     * Deliberately scoped to elements that *claim* a width rather than applied
     * to every table. A message that never asked for one is already fluid, and
     * rewriting its layout would be changing mail that renders correctly.
     *
     * This does not fix everything, and the limit is worth naming: content whose
     * min-content width genuinely exceeds the window -- a 900px image with fixed
     * dimensions, a long unbroken URL, a \`white-space: nowrap\` rule of the
     * sender's own -- still overflows, because no amount of CSS can make an
     * indivisible thing narrower. Those want scaling, which needs measurement,
     * which this frame deliberately cannot do: it runs with no scripts and no
     * same-origin, so nothing can read its content width. See \`ui/Reader.tsx\`.
     */
    table[width]:not([width$="%"]),
    td[width]:not([width$="%"]),
    th[width]:not([width$="%"]),
    col[width]:not([width$="%"]),
    colgroup[width]:not([width$="%"]) {
      width: auto !important;
      max-width: 100% !important;
    }
    table[style*="width"], td[style*="width"], th[style*="width"] {
      max-width: 100% !important;
    }
    /*
     * Percentages are exempt, and the exemption is not a nicety.
     *
     * \`width="100%"\` is the one width in old mail that is already correct: it
     * says "fill whatever you are in". Rewriting it to \`auto\` makes the table
     * size to its *contents* instead, which is how a rule meant to narrow things
     * ends up widening them. Only pixel widths are the problem, and only pixel
     * widths are touched.
     */

    /* The hard width cap is not here -- see {@link hardCap}, which has to come
       after the message's own stylesheets to survive them. */
    /*
     * A long URL in a plain-text message is one word to the line breaker, so it
     * sets the document's minimum width all by itself. \`anywhere\` rather than
     * \`break-word\`, because only \`anywhere\` is allowed to break a word while
     * *calculating* min-content -- which is the number that decides whether this
     * document is wider than the window.
     */
    body { overflow-wrap: anywhere; }
    /*
     * The backstop, and it is a backstop rather than the fix.
     *
     * Whatever survives everything above is clipped here instead of being handed
     * to the reader as a sideways scroll. That loses the right-hand edge of a
     * stubborn message, which is a real cost -- but the thing it replaces is a
     * window that slides away from under the app, and between "part of this
     * message is off screen" and "the whole app moves sideways" the first is the
     * one somebody can work with.
     */
    html { overflow-x: hidden; }
    a { color: ${dark ? "#7fb2ff" : "#1a58c8"}; }
    /* The message's own header, for print only -- see the note on this function. */
    .hm-head {
      display: none !important;
      gap: 14px !important;
      align-items: flex-start !important;
      background: ${dark ? "#15171c" : "#ffffff"} !important;
      margin: -16px -20px 18px !important;
      padding: 16px 20px 16px !important;
      border-bottom: 1px solid ${dark ? "#2a2f37" : "#e3e6ea"} !important;
      font: 13px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
      color: ${dark ? "#e6e8ec" : "#16181d"} !important;
      /* Reset the inheritable text properties a sender's body leaks in. */
      text-align: left !important;
      text-transform: none !important;
      letter-spacing: normal !important;
      word-spacing: normal !important;
      text-indent: 0 !important;
      white-space: normal !important;
      direction: ltr !important;
    }
    .hm-head .hm-avatar {
      flex: none !important;
      width: 40px !important; height: 40px !important;
      border-radius: 50% !important;
      display: flex !important; align-items: center !important; justify-content: center !important;
      color: #fff !important; font-weight: 600 !important; font-size: 14px !important;
      letter-spacing: .02em !important;
    }
    .hm-head .hm-meta { min-width: 0 !important; flex: 1 !important; }
    /*
     * Name left, date right: two things people look for, each in a fixed place.
     *
     * Wrapping rather than a breakpoint, because the width that matters is this
     * frame's and not the app's -- a reading pane beside a list can be narrower
     * than a phone. The name is given a basis of about a dozen characters, so
     * when the two no longer fit it is the *date* that drops to its own line.
     * Left to itself the name shrank instead: at 344px "Priyanka Khare
     * Chaudhari" wrapped to three lines beside a date with room to spare.
     */
    .hm-head .hm-top {
      display: flex !important;
      flex-wrap: wrap !important;
      align-items: baseline !important;
      justify-content: space-between !important;
      gap: 0 12px !important;
      margin: 0 0 1px !important;
    }
    .hm-head .hm-name {
      flex: 1 1 14ch !important;
      font-weight: 650 !important; font-size: 15px !important;
      min-width: 0 !important; overflow-wrap: anywhere !important;
    }
    .hm-head .hm-date {
      flex: none !important;
      color: ${dark ? "#9aa1ab" : "#5b6270"} !important;
      font-size: 12px !important; font-variant-numeric: tabular-nums !important;
    }
    .hm-head .hm-from {
      color: ${dark ? "#9aa1ab" : "#5b6270"} !important;
      margin: 0 0 8px !important; overflow-wrap: anywhere !important;
    }
    /*
     * The recipient rows, as a two-column grid: labels in a narrow column and
     * every address starting at the same x. Scanning down one column is the
     * point -- the old single line ran "to a, b, c . cc d . account e" and the
     * separators were the only thing distinguishing three different kinds of
     * fact from each other.
     *
     * A max-content column rather than a fixed width, so it is exactly as wide
     * as the longest label and no wider, which on a phone is the difference
     * between an address column and a hyphenated one.
     */
    .hm-head .hm-rows {
      display: grid !important;
      grid-template-columns: max-content minmax(0, 1fr) !important;
      gap: 2px 10px !important;
      margin: 0 !important;
      font-size: 12.5px !important;
    }
    .hm-head .hm-rows dt {
      color: ${dark ? "#7b828c" : "#767d88"} !important;
      font-weight: 500 !important;
    }
    .hm-head .hm-rows dd {
      margin: 0 !important;
      color: ${dark ? "#c7cbd2" : "#3b414a"} !important;
      overflow-wrap: anywhere !important;
    }
    /* Clearance for the reader's floating toolbar -- see the note on this function. */
    .hm-dock-space { display: none; }
    @media (max-width: 47.9375rem) and (min-width: 26rem) {
      .hm-dock-space { display: block !important; height: 92px !important; }
    }
    blockquote {
      margin: 0 0 0 2px; padding-left: 12px;
      border-left: 2px solid ${dark ? "#3a4048" : "#d5d9e0"};
      color: ${dark ? "#a8aeb8" : "#5b6270"};
    }
    @media print {
      html, body { background: #fff !important; color: #16181d !important; }
      a { color: #1a58c8 !important; }
      .hm-head { display: flex !important; }
    }
  `;
}

/**
 * The width cap, last in the document and deliberately hard to outrank.
 *
 * **Everything above can be overruled by the message, and is.** A message
 * brings its own `<style>` elements, they are author-origin exactly like ours,
 * and they come later in the document -- so a marketing template carrying the
 * near-universal `img { max-width: none !important }` reset beats an identical
 * `!important` of ours on source order alone. That is not a hypothetical: the
 * message this was written against resolved to `max-width: none` with our rule
 * sitting right there in the head, which is why it still overflowed a 312px
 * screen by 28px after the first attempt at this.
 *
 * Two things fix that and both are needed. This block is emitted **after** the
 * message body, so it wins any tie on order; and it hangs off an id on `body`,
 * which puts it at a specificity mail effectively never reaches -- email
 * templates style by tag, class and inline attribute, and an id selector
 * outranks every one of those regardless of where it sits.
 *
 * # Why the cap is `100vw` rather than `100%`
 *
 * `max-width: 100%` resolves against the containing block, and that is circular
 * exactly when it matters: a 320px image in a table cell has already stretched
 * the cell to 320px, so "100% of my parent" is 320px, the constraint is
 * satisfied, and the document is still wider than the window. `100vw` is the
 * frame's own viewport, so it asks the only question worth asking -- is this
 * wider than the window -- instead of asking a parent that is part of the
 * problem. The 40px is this stylesheet's own body padding.
 *
 * On a desktop reading pane `100vw` is that column, which is the right answer
 * there too: a 900px hero in a 600px pane is the same bug at a different size.
 */
function hardCap(): string {
  return `
    #hm-body img, #hm-body video, #hm-body table, #hm-body pre,
    #hm-body blockquote, #hm-body figure, #hm-body object, #hm-body iframe {
      max-width: calc(100vw - 40px) !important;
      /*
       * Or the cap is not a cap. \`max-width\` bounds the *content* box, so a
       * table honouring 272px and then adding \`cellpadding\` on top is both
       * obeying the rule and overflowing the window -- which is precisely what
       * the second measurement of the real message showed: sixteen elements
       * still over the edge, none of them claiming a width, the widest a \`td\`
       * whose own padding put it there.
       */
      box-sizing: border-box !important;
    }
    #hm-body td, #hm-body th { box-sizing: border-box !important; }
    #hm-body img { height: auto !important; }
    /* Pixel widths only. \`width="100%"\` is the one width in old mail that is
       already right, and rewriting it to \`auto\` makes a table size to its
       contents -- a rule meant to narrow things, widening them. */
    #hm-body table[width]:not([width$="%"]),
    #hm-body td[width]:not([width$="%"]),
    #hm-body th[width]:not([width$="%"]) {
      width: auto !important;
      max-width: calc(100vw - 40px) !important;
    }
  `;
}

function wrap(inner: string, dark: boolean, allowRemoteImages: boolean, header = ""): string {
  return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${csp(allowRemoteImages)}">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="color-scheme" content="${dark ? "dark" : "light"}">
<base target="_blank" rel="noopener noreferrer">
<style>${frameStyle(dark)}</style></head><body id="hm-body">${header}${inner}<div class="hm-dock-space" aria-hidden="true"></div><style>${hardCap()}</style><script>${FIT_SCRIPT}</script></body></html>`;
}

/**
 * What the in-message header says. Every field is plain text and is escaped.
 *
 * The reader draws the on-screen header from the same facts, as a component;
 * this shape is what the printed copy inside the frame is built from.
 */
export interface HeaderFields {
  initials: string;
  /** Hue for the avatar disc, from the same `hueFor` the list uses. */
  hue: number;
  name: string;
  /** Shown only when {@link name} is not already the address. */
  address?: string;
  date: string;
  /** Machine-readable, for the `title`. */
  dateExact: string;
  to: string;
  cc?: string;
  /** Named in full where there is more than one account. */
  account?: string;
}

/**
 * The sender block, as markup for the message's own document -- where it is
 * shown only when the frame prints (see the module note).
 *
 * Here rather than in a component because it shares a document with hostile
 * HTML and every value in it has to be escaped on the way in; keeping that in
 * the module that owns escaping is the point. It renders no buttons and no
 * links: anything clickable stays in the app's document, out of the sender's
 * reach.
 *
 * The account is named rather than coloured. The dot the app draws elsewhere is
 * a `var(--acct-*)` custom property, and those are defined in the app's
 * stylesheet -- referenced in here it resolves to nothing and takes its whole
 * declaration with it, silently. A name that renders beats a colour that does
 * not.
 */
export function messageHeader(f: HeaderFields): string {
  const e = escapeHtml;
  const hue = Number.isFinite(f.hue) ? Math.round(f.hue) : 0;

  /*
   * A definition list, not more `div`s in a row.
   *
   * "To", "Cc" and the account are label/value pairs, and saying so in the
   * markup is what lets the values line up in a column -- which is the whole
   * readability win, because the eye finds the addresses in one place instead
   * of scanning a run-on sentence for the next separator. A screen reader gets
   * the same structure for free: "To, definition, amit@..." rather than one
   * long string of addresses and middots.
   */
  const rows = [
    ["To", f.to],
    ["Cc", f.cc],
    ["Account", f.account],
  ]
    .filter(([, value]) => Boolean(value))
    .map(([label, value]) => `<dt>${e(label as string)}</dt><dd>${e(value as string)}</dd>`)
    .join("");

  // Only when it says something the name did not. A sender whose display name
  // *is* their address would otherwise be printed twice, one line apart.
  const address = f.address ? `<div class="hm-from">${e(f.address)}</div>` : "";

  return (
    `<div class="hm-head">` +
    `<div class="hm-avatar" style="background: hsl(${hue} 55% 45%)">${e(f.initials)}</div>` +
    `<div class="hm-meta">` +
    `<div class="hm-top">` +
    `<span class="hm-name">${e(f.name)}</span>` +
    // Right-aligned and on the sender's line: it is the second thing anybody
    // looks for, and a fixed place to look for it beats reading to the end.
    `<span class="hm-date" title="${e(f.dateExact)}">${e(f.date)}</span>` +
    `</div>` +
    address +
    (rows ? `<dl class="hm-rows">${rows}</dl>` : "") +
    `</div></div>`
  );
}

export function renderHtmlBody(
  html: string,
  dark: boolean,
  allowRemoteImages: boolean,
  header = "",
  darkenMail = false,
): RenderedBody {
  /*
   * Dark for a message that left its colours to us; dark for one that brought
   * them only if it is rewritten first.
   *
   * The frame's `html, body { color; background }` is not `!important` and
   * never can be -- a newsletter that styled every cell must keep its design.
   * But the cascade cuts both ways: a message that sets `color:#000` on its
   * paragraphs and nothing on its background wins the text colour and loses
   * the background, and in a dark frame that is black on near-black -- the
   * illegible mail Amit reported from the phone. Every such message assumed
   * the canvas mail has always had, which is white. So a message that brought
   * any colour at all is read on white regardless of the app's theme, and only
   * a message that brought none -- most human-written mail, and everything
   * plain text -- takes the theme. Plain text never asks: `renderTextBody`
   * writes no colours of its own for a sender to fight.
   *
   * `darkenMail` is the second answer to the same problem: rather than give a
   * coloured message the white canvas it assumed, rewrite the colours it
   * declared for a dark one -- see `darkenColours` for what that means and
   * what it costs -- after which the frame can be themed like any other, since
   * the message no longer says `color:#000` anywhere. The reader passes it
   * when the app is dark and this message has not been switched back to its
   * own colours. It is a request, not a switch: a message that brought no
   * colours has nothing to rewrite and is themed as before.
   */
  let body = html;
  let themed = dark && !bringsOwnColours(html);
  if (dark && darkenMail && !themed) {
    body = darkenColours(html);
    themed = true;
  }
  return {
    srcdoc: wrap(body, themed, allowRemoteImages, header),
    hasRemoteContent: REMOTE.test(html) || REMOTE_CSS.test(html),
  };
}

/**
 * A plain-text body, escaped and lightly marked up.
 *
 * Quote levels get a class so a long reply chain reads as a chain. URLs become
 * links because a plain-text message full of unclickable URLs is the one place
 * where doing nothing is clearly worse than doing something.
 */
export function renderTextBody(text: string, dark: boolean, header = ""): RenderedBody {
  const lines = text.split(/\r?\n/u).map((line) => {
    const depth = /^(\s*>)+/u.exec(line)?.[0].split(">").length ?? 1;
    const body = linkify(escapeHtml(line));
    return depth > 1
      ? `<div class="q q${Math.min(depth - 1, 3)}">${body || "&nbsp;"}</div>`
      : `<div>${body || "&nbsp;"}</div>`;
  });
  const style = `
    body { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13.5px; }
    .q { border-left: 2px solid ${dark ? "#3a4048" : "#d5d9e0"}; padding-left: 10px; color: ${dark ? "#a8aeb8" : "#5b6270"}; }
    .q2 { opacity: .85 } .q3 { opacity: .7 }
  `;
  return {
    srcdoc: wrap(`<style>${style}</style>${lines.join("")}`, dark, false, header),
    hasRemoteContent: false,
  };
}

const URL_RE = /\bhttps?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)]/gu;

function linkify(escaped: string): string {
  return escaped.replace(URL_RE, (url) => `<a href="${url}">${url}</a>`);
}
